"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { computeShiftReportTotals } from "@/modules/merchant/presentation/lib/shift-report-stats";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { ShopShiftReportFilters } from "@/modules/merchant/presentation/ui/shifts/ShopShiftReportFilters";
import { ShopShiftReportPanels } from "@/modules/merchant/presentation/ui/shifts/ShopShiftReportPanels";
import { ShopShiftReportToolbar } from "@/modules/merchant/presentation/ui/shifts/ShopShiftReportToolbar";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useParams } from "@tanstack/react-router";
import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  startOfWeek,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useApproveShopShiftSwapRequest,
  useRejectShopShiftSwapRequest,
  useShopBranchesListQuery,
  useShopShiftReportSummaryQuery,
  useShopShiftSwapRequestsListQuery,
} from "../api/queries";

export function ShopShiftReportPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const [weekOffset, setWeekOffset] = useState(0);
  const [branchId, setBranchId] = useState<string | undefined>(undefined);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [scrollToDayKey, setScrollToDayKey] = useState<string | null>(null);

  const branchesQuery = useShopBranchesListQuery(shopId);
  const branches = branchesQuery.data ?? [];

  useEffect(() => {
    if (branches.length > 0 && !branchId) {
      setBranchId(branches[0]?.id);
    }
  }, [branches, branchId]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(addWeeks(new Date(), weekOffset), {
      weekStartsOn: 1,
    });
    return eachDayOfInterval({
      start,
      end: endOfWeek(start, { weekStartsOn: 1 }),
    });
  }, [weekOffset]);

  const range = useMemo(() => {
    const from = format(weekDays[0] ?? new Date(), "yyyy-MM-dd");
    const to = format(weekDays[6] ?? new Date(), "yyyy-MM-dd");
    return { from, to };
  }, [weekDays]);

  const reportQuery =
    branchId != null ? { branchId, from: range.from, to: range.to } : null;

  const report = useShopShiftReportSummaryQuery(shopId, reportQuery);

  const swapPendingQuery = useShopShiftSwapRequestsListQuery(
    shopId,
    branchId != null ? { branchId, status: "pending" } : null,
  );

  const approveSwap = useApproveShopShiftSwapRequest(shopId);
  const rejectSwap = useRejectShopShiftSwapRequest(shopId);

  const byDay = report.data?.byDay ?? [];
  const byStaff = report.data?.byStaff ?? [];
  const swapPending = swapPendingQuery.data?.items ?? [];

  const countsByDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const day of weekDays) {
      m.set(format(day, "yyyy-MM-dd"), 0);
    }
    for (const row of byDay) {
      m.set(row.workDate, row.assignmentCount);
    }
    return m;
  }, [byDay, weekDays]);

  const totals = useMemo(
    () => computeShiftReportTotals(byDay, byStaff, swapPending.length),
    [byDay, byStaff, swapPending.length],
  );

  const weekLabel = `${formatDateLocal(range.from)} – ${formatDateLocal(range.to)}`;
  const branchName = branches.find((b) => b.id === branchId)?.name ?? null;

  const handleSelectDay = useCallback((dateKey: string) => {
    setSelectedDayKey(dateKey);
    setScrollToDayKey(dateKey);
  }, []);

  useEffect(() => {
    setSelectedDayKey(null);
    setScrollToDayKey(null);
  }, [weekOffset, branchId]);

  const handleRefresh = useCallback(() => {
    void report.refetch();
    void swapPendingQuery.refetch();
  }, [report, swapPendingQuery]);

  const loading =
    Boolean(branchId) &&
    (report.isLoading || swapPendingQuery.isLoading);

  return (
    <RequireMerchantPermission permission={MerchantPermissions.shifts.manage}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.shiftReport(shopId)} />
      </Header>

      <Main>
        <ShopShiftReportToolbar
          shopId={shopId}
          branchName={branchName}
          weekLabel={weekLabel}
          totals={totals}
          isRefreshing={
            (report.isFetching && !report.isLoading) ||
            (swapPendingQuery.isFetching && !swapPendingQuery.isLoading)
          }
          onRefresh={handleRefresh}
        />

        <ShopShiftReportFilters
          branchId={branchId}
          branches={branches}
          onBranchChange={setBranchId}
          weekDays={weekDays}
          weekLabel={weekLabel}
          onWeekOffsetChange={setWeekOffset}
          countsByDate={countsByDate}
          selectedDayKey={selectedDayKey}
          onSelectDay={handleSelectDay}
        />

        {!branchId ? (
          <section className="rounded-xl border border-border bg-card px-6 py-12 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">
              ເລືອກສາຂາເພື່ອເບິ່ງລາຍງານ
            </p>
          </section>
        ) : (
          <ShopShiftReportPanels
            loading={loading}
            totals={totals}
            byDay={byDay}
            byStaff={byStaff}
            weekDays={weekDays}
            swapPending={swapPending}
            swapBusy={approveSwap.isPending || rejectSwap.isPending}
            onApproveSwap={(id) => void approveSwap.mutateAsync(id)}
            onRejectSwap={(id) => void rejectSwap.mutateAsync(id)}
            highlightDayKey={selectedDayKey}
            scrollToDayKey={scrollToDayKey}
            onScrolledToDay={() => setScrollToDayKey(null)}
          />
        )}
      </Main>
    </RequireMerchantPermission>
  );
}
